import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import parser from 'html-react-parser';
import { Button as MuiButton, Grid } from '@mui/material';
import {
  ButtonBack,
  ButtonNext,
  CarouselContext,
  CarouselProvider,
  Slide,
  Slider as CarouselSlider
} from 'pure-react-carousel';
import qs from 'query-string';
import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds
} from 'date-fns';
import { ChevronLeft, ChevronRight, FileEarmarkTextFill } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useGetHomeSlider, useGetHomePromotionCountdown, useGetHomeArticlesGroup } from 'api';
import { Link } from 'components/controls';
import WiseModal from 'components/wise/WiseModal';
import { useSelector } from 'store';

import styles from 'theme/components/wise/WiseSlider/WiseSlider.module.scss';

const AUTOPLAY_INTERVAL_MS = 5000;

const WiseSliderAutoplayController: React.FC<{ enabled: boolean; slidesCount: number }> = ({
  enabled,
  slidesCount
}) => {
  const carouselStore = useContext(CarouselContext);

  useLayoutEffect(() => {
    if (!carouselStore || !enabled || slidesCount <= 1) {
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        const s = carouselStore.getStoreState();
        const next = (s.currentSlide + 1) % slidesCount;
        carouselStore.setStoreState({ currentSlide: next });
      }, AUTOPLAY_INTERVAL_MS);
    };

    const handleSlideChange = () => {
      scheduleNext();
    };

    scheduleNext();
    carouselStore.subscribe(handleSlideChange);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      carouselStore.unsubscribe(handleSlideChange);
    };
  }, [carouselStore, enabled, slidesCount]);

  return null;
};

const WiseSliderNavControls: React.FC = () => {
  const carouselStore = useContext(CarouselContext);

  if (!carouselStore) {
    return null;
  }

  return (
    <div className={styles.nav}>
      <ButtonBack className={styles.navButton}>
        <ChevronLeft />
      </ButtonBack>
      <ButtonNext className={styles.navButton}>
        <ChevronRight />
      </ButtonNext>
    </div>
  );
};

const WiseSliderCta: React.FC<{ items: { name: string; url: string; new_tab: boolean }[] }> = ({
  items
}) => {
  const carouselStore = useContext(CarouselContext);
  const [current, setCurrent] = useState(0);

  useLayoutEffect(() => {
    if (!carouselStore) {
      return undefined;
    }

    const sync = () => {
      const s = carouselStore.getStoreState();
      setCurrent(s.currentSlide ?? 0);
    };

    sync();
    carouselStore.subscribe(sync);
    return () => carouselStore.unsubscribe(sync);
  }, [carouselStore]);

  const active = useMemo(() => items[current], [items, current]);
  if (!active?.url || !active?.name) {
    return null;
  }

  return (
    <MuiButton
      className={styles.cta}
      variant="contained"
      component="a"
      href={active.url}
      target={active.new_tab ? '_blank' : '_self'}
      rel={active.new_tab ? 'noreferrer' : undefined}>
      {active.name}
    </MuiButton>
  );
};

const WiseSliderSlideIndicator: React.FC<{ slidesCount: number }> = ({ slidesCount }) => {
  const carouselStore = useContext(CarouselContext);
  const [current, setCurrent] = useState(0);
  const [progressSeed, setProgressSeed] = useState(0);

  useLayoutEffect(() => {
    if (!carouselStore) {
      return undefined;
    }

    const sync = () => {
      const s = carouselStore.getStoreState();
      setCurrent(s.currentSlide ?? 0);
      setProgressSeed((x) => x + 1);
    };

    sync();
    carouselStore.subscribe(sync);
    return () => carouselStore.unsubscribe(sync);
  }, [carouselStore]);

  if (!carouselStore || slidesCount <= 1) {
    return null;
  }

  return (
    <div
      className={styles.indicator}
      style={{ ['--wise-slide-progress-ms' as any]: `${AUTOPLAY_INTERVAL_MS}ms` }}
      aria-hidden>
      {Array.from({ length: slidesCount }).map((_, i) => (
        <div
          key={i === current ? `${i}-active-${current}-${progressSeed}` : i}
          className={classnames(
            styles.indicatorDot,
            i === current && styles.indicatorDotActive
          )}
          onClick={() => carouselStore.setStoreState({ currentSlide: i })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              carouselStore.setStoreState({ currentSlide: i });
            }
          }}
        />
      ))}
    </div>
  );
};

export const WiseSlider = () => {
  const { isChatOpen, isChatDocked } = useSelector((state) => state.chat);

  const [promotionCountdown, setPromotionCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  // Artykuł aktualności wybrany do podglądu w modalu (kliknięcie w sidebar listy news).
  const [activeArticle, setActiveArticle] = useState<
    { id: number; title: string; text?: string } | null
  >(null);

  const { data: homeSliderData } = useGetHomeSlider();
  const { data: homeHomeArticlesGroupData } = useGetHomeArticlesGroup('HOME_NEWS');
  const { data: homePromotionCountdownData } = useGetHomePromotionCountdown();

  useEffect(() => {
    if (homePromotionCountdownData?.finish_datetime) {
      const interval = setInterval(updatePromotionCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [homePromotionCountdownData?.finish_datetime]);

  const updatePromotionCountdown = () => {
    if (homePromotionCountdownData?.finish_datetime) {
      const currentDate = addMinutes(new Date(), new Date().getTimezoneOffset());
      const endPromotionDate = new Date(homePromotionCountdownData.finish_datetime);

      if (differenceInSeconds(endPromotionDate, currentDate) < 0) {
        return setPromotionCountdown({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
      }

      const days = differenceInDays(endPromotionDate, currentDate);
      const hours = differenceInHours(endPromotionDate, addDays(currentDate, days));
      const minutes = differenceInMinutes(
        endPromotionDate,
        addHours(addDays(currentDate, days), hours)
      );
      const seconds = differenceInSeconds(
        endPromotionDate,
        addMinutes(addHours(addDays(currentDate, days), hours), minutes)
      );

      setPromotionCountdown({
        days,
        hours,
        minutes,
        seconds
      });
    }
  };

  const numberClassName = classnames(styles.number, {
    [styles.numberDocked]: isChatDocked && isChatOpen
  });

  const renderSidebar = () => (
    <Grid item xs={12} md={4} lg={3.8}>
      <div className={classnames(styles.sidebar, styles.sidebarShell)}>
        <div className={styles.newsBlock}>
          <Link to="/aktualnosci" className={styles.newsTitle}>
            {homeHomeArticlesGroupData?.title}
          </Link>
          <div className={styles.newsWrapper}>
            {homeHomeArticlesGroupData?.articles.map((article) => (
              <div
                className={styles.news}
                key={article.id}
                onClick={() =>
                  setActiveArticle({ id: article.id, title: article.title, text: article.text })
                }>
                <div className={styles.newsIcon} aria-hidden>
                  <FileEarmarkTextFill />
                </div>
                <span className={styles.newsText}>{article.title}</span>
                <div className={styles.newsChevron} aria-hidden>
                  <ChevronRight />
                </div>
              </div>
            ))}
          </div>
        </div>

        {promotionCountdown && (
          <div className={styles.counterWrapper}>
            <div className={styles.counterTitle}>{homePromotionCountdownData?.title}</div>
            <div className={styles.counter}>
              <span className={numberClassName}>{Math.floor(promotionCountdown.hours / 10)}</span>
              <span className={numberClassName}>{promotionCountdown.hours % 10}</span>
              <span>:</span>
              <span className={numberClassName}>{Math.floor(promotionCountdown.minutes / 10)}</span>
              <span className={numberClassName}>{promotionCountdown.minutes % 10}</span>
              <span>:</span>
              <span className={numberClassName}>{Math.floor(promotionCountdown.seconds / 10)}</span>
              <span className={numberClassName}>{promotionCountdown.seconds % 10}</span>
            </div>
          </div>
        )}
      </div>
    </Grid>
  );

  const renderSlider = () => (
    <Grid item xs={12} md={8} lg={8.2}>
      {homeSliderData && (
        <div className={classnames(styles.slider, styles.sliderShell)}>
          <CarouselProvider
            naturalSlideWidth={0}
            naturalSlideHeight={0}
            totalSlides={homeSliderData?.items.length || 0}
            visibleSlides={1}
            step={1}>
            <WiseSliderAutoplayController
              enabled={homeSliderData.items.length > 1}
              slidesCount={homeSliderData.items.length}
            />
            {homeSliderData.items.length > 1 && <WiseSliderNavControls />}
            <CarouselSlider>
              {(homeSliderData?.items || []).map((slideData, index) => (
                <Slide
                  key={slideData.id}
                  index={index}
                  className={styles.slide}
                  style={
                    index === 0 ? undefined : { backgroundImage: `url('${slideData.image}')` }
                  }>
                  {index === 0 ? (
                    slideData.url ? (
                      <a
                        href={slideData.url}
                        target={slideData.new_tab ? '_blank' : '_self'}
                        className={styles.slideLink}
                        rel="noreferrer">
                        <img
                          src={slideData.image}
                          alt=""
                          className={styles.slideImage}
                          loading="eager"
                          {...{ fetchPriority: 'high' }}
                        />
                      </a>
                    ) : (
                      <Link
                        to={`/products?${qs.stringify(
                          {
                            category_id: slideData.category_id,
                            search_keyword: slideData.search_keyword,
                            sort_method: slideData.sort_method
                          },
                          { skipNull: true }
                        )}`}
                        target={slideData.new_tab ? '_blank' : '_self'}
                        className={styles.slideLink}>
                        <img
                          src={slideData.image}
                          alt=""
                          className={styles.slideImage}
                          loading="eager"
                          {...{ fetchPriority: 'high' }}
                        />
                      </Link>
                    )
                  ) : slideData.url ? (
                    <a
                      href={slideData.url}
                      target={slideData.new_tab ? '_blank' : '_self'}
                      className={styles.slideLink}
                      rel="noreferrer"
                    />
                  ) : (
                    <Link
                      to={`/products?${qs.stringify(
                        {
                          category_id: slideData.category_id,
                          search_keyword: slideData.search_keyword,
                          sort_method: slideData.sort_method
                        },
                        { skipNull: true }
                      )}`}
                      target={slideData.new_tab ? '_blank' : '_self'}
                      className={styles.slideLink}
                    />
                  )}
                </Slide>
              ))}
            </CarouselSlider>

            <WiseSliderSlideIndicator slidesCount={homeSliderData.items.length} />
            <WiseSliderCta
              items={homeSliderData.items.map((x) => ({
                name: x.name,
                url: x.url,
                new_tab: x.new_tab
              }))}
            />
          </CarouselProvider>
        </div>
      )}
    </Grid>
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        styles.root,
        'StylePath-Pages-Home-components-Banner-WiseSlider'
      )}>
      <Grid container spacing={1}>
        {renderSlider()}
        {renderSidebar()}
      </Grid>

      <WiseModal
        open={activeArticle !== null}
        onClose={() => setActiveArticle(null)}
        title={activeArticle?.title}
        maxWidth={640}>
        <div className={styles.newsDialogContent}>
          {activeArticle?.text ? parser(activeArticle.text) : null}
        </div>
      </WiseModal>
    </div>
  );
};

export default WiseSlider;
